import React, { useEffect, useState } from 'react';
import Amplify, { API, graphqlOperation } from 'aws-amplify';
import {
  Row,
  Col,
  Switch,
  Divider,
  Input,
  Button,
  Select,
  Table,
  Tag,
  Space,
  message,
} from 'antd';
import { SearchOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { MainLayout } from '../../components/Layout';
import { Admin, Shared } from '../../queries';

const { Option } = Select;

export default function AssistantAccess(props) {
  const [loading, setLoading] = useState(false);
  const [systemConfig, setSystemConfig] = useState(null);

  // Whitelist state.
  const [whitelist, setWhitelist] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);

  // User search state.
  const [searchType, setSearchType] = useState('mobile');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const getSystemConfig = async () => {
    try {
      setLoading(true);
      const res = await API.graphql(
        graphqlOperation(Shared.SystemConfig.GET_SYSTEM_CONFIG, {})
      );
      setSystemConfig(res.data.findFirstSystemConfig);
    } catch (error) {
      console.log('AssistantAccess ~ getSystemConfig ~ error', error);
      message.error('Failed to load system config');
    } finally {
      setLoading(false);
    }
  };

  const getWhitelist = async () => {
    try {
      setWhitelistLoading(true);
      const res = await API.graphql(
        graphqlOperation(Admin.AssistantWhitelist.GET_ASSISTANT_WHITELIST, {
          orderBy: { createdAt: 'desc' },
        })
      );
      setWhitelist(res.data.assistantWhitelistedUsers || []);
    } catch (error) {
      console.log('AssistantAccess ~ getWhitelist ~ error', error);
      message.error('Failed to load whitelist');
    } finally {
      setWhitelistLoading(false);
    }
  };

  useEffect(() => {
    const { token } = props;
    Amplify.configure({
      API: {
        graphql_headers: async () => ({
          Authorization: `Bearer ${token}`,
        }),
      },
    });
    getSystemConfig();
    getWhitelist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAllowAssistant = async (value) => {
    if (!systemConfig) return;
    try {
      await API.graphql(
        graphqlOperation(Admin.SystemConfig.UPDATE_SYSTEM_CONFIG, {
          where: { id: systemConfig.id },
          data: { allowAssistant: { set: value } },
        })
      );
      message.success(
        value
          ? 'Assistant is now visible to everyone'
          : 'Assistant restricted to the whitelist'
      );
      await getSystemConfig();
    } catch (error) {
      console.log('AssistantAccess ~ toggleAllowAssistant ~ error', error);
      message.error('Failed to update the flag');
    }
  };

  const searchUsers = async () => {
    const value = (searchValue || '').trim();
    if (!value) {
      message.warning('Enter a value to search');
      return;
    }
    try {
      setSearching(true);
      const where = { [searchType]: { contains: value } };
      const res = await API.graphql(
        graphqlOperation(Admin.User.GET_ALL_USERS, {
          where,
          orderBy: { createdAt: 'desc' },
          take: 10,
          skip: 0,
        })
      );
      setSearchResults(res.data.users || []);
    } catch (error) {
      console.log('AssistantAccess ~ searchUsers ~ error', error);
      message.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addToWhitelist = async (userId) => {
    try {
      await API.graphql(
        graphqlOperation(
          Admin.AssistantWhitelist.ADD_ASSISTANT_WHITELIST_USER,
          { userId }
        )
      );
      message.success('User added to the whitelist');
      await getWhitelist();
    } catch (error) {
      console.log('AssistantAccess ~ addToWhitelist ~ error', error);
      message.error(
        'Failed to add user (they may already be whitelisted)'
      );
    }
  };

  const removeFromWhitelist = async (id) => {
    try {
      await API.graphql(
        graphqlOperation(
          Admin.AssistantWhitelist.REMOVE_ASSISTANT_WHITELIST_USER,
          { where: { id } }
        )
      );
      message.success('User removed from the whitelist');
      await getWhitelist();
    } catch (error) {
      console.log('AssistantAccess ~ removeFromWhitelist ~ error', error);
      message.error('Failed to remove user');
    }
  };

  const whitelistedIds = new Set(whitelist.map((w) => w.user && w.user.id));

  const searchColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => <Tag color="blue">{role}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const already = whitelistedIds.has(record.id);
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            disabled={already}
            onClick={() => addToWhitelist(record.id)}
          >
            {already ? 'Added' : 'Add'}
          </Button>
        );
      },
    },
  ];

  const whitelistColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (record.user && record.user.name) || '-',
    },
    {
      title: 'Mobile',
      key: 'mobile',
      render: (_, record) => (record.user && record.user.mobile) || '-',
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => (record.user && record.user.email) || '-',
    },
    {
      title: 'Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) =>
        createdAt ? String(createdAt).slice(0, 10) : '-',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeFromWhitelist(record.id)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <MainLayout title="Assistant Access">
      <Divider orientation="center">Global flag</Divider>
      <Row gutter={16} align="middle">
        <Col className="gutter-row" span={8}>
          <span style={{ padding: '25px', fontWeight: 'bolder' }}>
            Allow assistant for everyone:
          </span>
        </Col>
        <Col className="gutter-row" span={8}>
          <span
            style={{
              paddingRight: '10px',
              color:
                systemConfig && systemConfig.allowAssistant
                  ? '#41ce41'
                  : '#f34646',
              fontWeight: 500,
            }}
          >
            {systemConfig && systemConfig.allowAssistant
              ? ' Enabled (visible to all users)'
              : ' Disabled (whitelist only)'}
          </span>
          <Switch
            loading={loading}
            checked={!!(systemConfig && systemConfig.allowAssistant)}
            onChange={toggleAllowAssistant}
          />
        </Col>
      </Row>

      <Divider orientation="center">Whitelist — add a user</Divider>
      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <Select
            value={searchType}
            style={{ width: 140 }}
            onChange={(v) => setSearchType(v)}
          >
            <Option value="mobile">Mobile</Option>
            <Option value="name">Name</Option>
            <Option value="email">Email</Option>
            <Option value="id">User ID</Option>
          </Select>
        </Col>
        <Col flex="auto">
          <Input
            placeholder={`Search users by ${searchType}`}
            value={searchValue}
            allowClear
            onChange={(e) => setSearchValue(e.target.value)}
            onPressEnter={searchUsers}
            style={{ maxWidth: 360 }}
          />
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={searching}
            onClick={searchUsers}
          >
            Search
          </Button>
        </Col>
      </Row>
      {searchResults.length > 0 && (
        <Table
          columns={searchColumns}
          dataSource={searchResults}
          rowKey={(record) => record.id}
          pagination={false}
          loading={searching}
          size="small"
          style={{ marginBottom: 24 }}
        />
      )}

      <Divider orientation="center">Whitelisted users</Divider>
      <Space style={{ marginBottom: 12 }}>
        <Button onClick={getWhitelist} loading={whitelistLoading}>
          Refresh
        </Button>
      </Space>
      <Table
        columns={whitelistColumns}
        dataSource={whitelist}
        rowKey={(record) => record.id}
        loading={whitelistLoading}
        pagination={{ pageSize: 20 }}
      />
    </MainLayout>
  );
}
